import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HardHat,
  Loader2,
  ToggleLeft,
  ToggleRight,
  CheckCircle,
  Hash,
  Trash2,
  FileText,
  Calendar,
  Clock,
  Save,
} from 'lucide-react';
import { FormModal, MODAL_COLORS, useModal } from '@/components/modal';
import { useUpdateHelmetType, useDeactivateHelmetType } from '@/features/reports/hooks';
import { triggerHapticFeedback } from '../utils/adminAnimations';
import toast from 'react-hot-toast';
import type { HelmetTypeDefinition } from '@/features/reports/types';

const cardStyle = {
  backgroundColor: MODAL_COLORS.interactive,
  border: `1px solid ${MODAL_COLORS.border}`,
};

const actionBtnStyle = {
  border: `1px solid ${MODAL_COLORS.border}`,
};

const inputStyle = {
  backgroundColor: MODAL_COLORS.surface,
  border: `1px solid ${MODAL_COLORS.border}`,
  color: MODAL_COLORS.text,
};

interface HelmetTypeDetailModalProps {
  helmetType: HelmetTypeDefinition;
  onClose: () => void;
}

export function HelmetTypeDetailModal({ helmetType, onClose }: HelmetTypeDetailModalProps) {
  const { t } = useTranslation();
  const { openConfirm } = useModal();
  const updateMutation = useUpdateHelmetType();
  const deleteMutation = useDeactivateHelmetType();
  const [isOpen, setIsOpen] = useState(true);
  const requestClose = useCallback(() => setIsOpen(false), []);

  // Editable fields
  const [name, setName] = useState(helmetType.name);
  const [description, setDescription] = useState(helmetType.description ?? '');

  const hasChanges =
    name !== helmetType.name ||
    description !== (helmetType.description ?? '');

  const handleSave = async () => {
    if (!name.trim()) return;
    try {
      triggerHapticFeedback();
      await updateMutation.mutateAsync({
        id: helmetType.id,
        data: {
          name: name.trim(),
          description: description.trim() || undefined,
        },
      });
      toast.success(t('helmetsAdmin.updateSuccess'));
      requestClose();
    } catch {
      toast.error(t('helmetsAdmin.updateFailed'));
    }
  };

  const handleToggleActive = async () => {
    try {
      triggerHapticFeedback();
      await updateMutation.mutateAsync({
        id: helmetType.id,
        data: { isActive: !helmetType.isActive },
      });
      toast.success(
        t(helmetType.isActive ? 'helmetsAdmin.deactivateSuccess' : 'helmetsAdmin.activateSuccess')
      );
    } catch {
      toast.error(t('helmetsAdmin.updateFailed'));
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={requestClose}
      onExitComplete={onClose}
      title={helmetType.name}
      maxWidth="max-w-lg"
    >
      <div className="space-y-2">
        {/* Status */}
        <div className="rounded-xl p-3" style={cardStyle}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 opacity-60" />
              <span className="text-sm font-bold">{t('helmetsAdmin.status')}</span>
              <span className="text-sm font-semibold opacity-70">
                {helmetType.isActive ? t('helmetsAdmin.active') : t('helmetsAdmin.inactive')}
              </span>
            </div>
            <button
              onClick={handleToggleActive}
              disabled={updateMutation.isPending}
              className="flex items-center gap-1.5 px-2.5 py-1 glass-surface rounded-lg text-xs font-bold hover:bg-white/10 transition-colors disabled:opacity-50 cursor-pointer"
              style={actionBtnStyle}
            >
              {updateMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : helmetType.isActive ? (
                <>
                  <ToggleRight className="w-3.5 h-3.5" />
                  {t('helmetsAdmin.inactive')}
                </>
              ) : (
                <>
                  <ToggleLeft className="w-3.5 h-3.5" />
                  {t('helmetsAdmin.active')}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="rounded-xl p-2 text-center" style={cardStyle}>
          <HardHat className="w-5 h-5 mx-auto mb-1 opacity-60" />
          <p className="text-xl font-bold">{helmetType.reportCount ?? 0}</p>
          <p className="text-xs font-semibold opacity-60">{t('helmetsAdmin.reports')}</p>
        </div>

        {/* Code (read-only) */}
        <div className="rounded-xl p-3" style={cardStyle}>
          <div className="flex items-center gap-2 text-sm">
            <Hash className="w-3.5 h-3.5 opacity-50" />
            <span className="font-bold">{t('helmetsAdmin.code')}:</span>
            <span className="opacity-70 font-mono">{helmetType.code}</span>
          </div>
        </div>

        {/* Editable fields */}
        <div className="rounded-xl p-3 space-y-2.5" style={cardStyle}>
          {/* Name */}
          <div className="space-y-1">
            <label className="flex items-center gap-1.5 text-xs font-bold opacity-70">
              <HardHat className="w-3.5 h-3.5" />
              {t('helmetsAdmin.name')}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-400"
              style={inputStyle}
            />
          </div>

          {/* Description */}
          <div className="space-y-1">
            <label className="flex items-center gap-1.5 text-xs font-bold opacity-70">
              <FileText className="w-3.5 h-3.5" />
              {t('helmetsAdmin.description')}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 rounded-lg text-sm font-semibold resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
              style={inputStyle}
            />
          </div>
        </div>

        {/* Save button */}
        {hasChanges && (
          <button
            onClick={handleSave}
            disabled={updateMutation.isPending || !name.trim()}
            className="flex items-center justify-center gap-2 w-full px-3 py-2.5 rounded-xl font-bold transition-transform cursor-pointer disabled:opacity-50"
            style={{
              backgroundColor: MODAL_COLORS.header,
              border: `1px solid ${MODAL_COLORS.border}`,
              color: '#fff',
            }}
          >
            {updateMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {t('common.save')}
          </button>
        )}

        {/* Compatible Treatments */}
        <div className="rounded-xl p-3" style={cardStyle}>
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-4 h-4 opacity-60" />
            <span className="text-sm font-bold">{t('helmetsAdmin.compatibleTreatments')}</span>
          </div>
          {helmetType.compatibleTreatmentCodes?.length ? (
            <div className="flex flex-wrap gap-1.5">
              {helmetType.compatibleTreatmentCodes.map((code) => (
                <span
                  key={code}
                  className="px-2 py-0.5 rounded-lg text-xs font-semibold"
                  style={{ backgroundColor: MODAL_COLORS.surface, border: `1px solid ${MODAL_COLORS.border}` }}
                >
                  {code}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-xs opacity-50">{t('helmetsAdmin.noCompatibleTreatments')}</p>
          )}
        </div>

        {/* Dates */}
        <div className="rounded-xl p-3 space-y-1" style={cardStyle}>
          <div className="flex items-center gap-2 text-xs">
            <Calendar className="w-3.5 h-3.5 opacity-50" />
            <span className="font-bold">{t('helmetsAdmin.createdAt')}:</span>
            <span className="opacity-70">{new Date(helmetType.createdAt).toLocaleDateString()}</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <Clock className="w-3.5 h-3.5 opacity-50" />
            <span className="font-bold">{t('helmetsAdmin.updatedAt')}:</span>
            <span className="opacity-70">{new Date(helmetType.updatedAt).toLocaleDateString()}</span>
          </div>
        </div>

        {/* Delete */}
        <button
          onClick={() => {
            triggerHapticFeedback();
            openConfirm({
              title: t('helmetsAdmin.delete'),
              message: t('helmetsAdmin.deleteConfirm'),
              variant: 'danger',
              confirmLabel: t('common.delete'),
              onConfirm: async () => {
                try {
                  await deleteMutation.mutateAsync(helmetType.id);
                  toast.success(t('helmetsAdmin.deleteSuccess'));
                  requestClose();
                } catch {
                  toast.error(t('helmetsAdmin.deleteFailed'));
                }
              },
            });
          }}
          disabled={deleteMutation.isPending}
          className="flex items-center justify-center gap-2 w-full px-3 py-2.5 rounded-xl font-bold hover:translate-x-[1px] hover:translate-y-[1px] transition-transform cursor-pointer disabled:opacity-50"
          style={{
            backgroundColor: 'rgba(254, 226, 226, 0.6)',
            border: `1px solid ${MODAL_COLORS.border}`,
            color: '#B91C1C',
          }}
        >
          <Trash2 className="w-4 h-4" />
          {t('helmetsAdmin.delete')}
        </button>
      </div>
    </FormModal>
  );
}
